import React from "react";
import AotdHand from "../hand/AotdHand";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import {
  useGetMyCardsQuery,
  useGetPublicActiveEffectsQuery,
  useGetSurfaceCardFeedQuery,
} from "../api/respectCardsApi";
import type {
  CardDefinition,
  CardInstance,
  OwnedCardTile,
  PublicActiveEffect,
  SurfaceCardFeedItem,
} from "../types/cardTypes";
import RespectCard from "../components/RespectCard";
import HandStrip from "../components/HandStrip";
import { isInHand } from "../lib/cardState";
import { useAotdCardPlay } from "./AotdCardPlayContext";
import { humanizeUntil, formatEffectRemaining, formatRelativeTime } from "../lib/utils";
import { useNowTicker } from "../hooks/useNowTicker";
import "../styles/cards.css";

// `aotd.notification` joined the list with the mute rework: Radio Silence is an
// AOTD card now (it takes one unlock's reminder away), so its gate belongs in
// the banner and its copies belong in the page's hand row.
const AOTD_SCOPES = ["aotd.claim", "aotd.race", "aotd.comment", "aotd.notification"];
const AOTD_SCOPE_SET = new Set(AOTD_SCOPES);

/** Playable-now = server playability `ready`/`needs_target` (a Sprint Lead sitting
 *  past its scheduled time is NOT playable, so it never inflates the count). */
function isPlayableNow(tile: OwnedCardTile): boolean {
  const s = tile.playability?.state;
  return s === "ready" || s === "needs_target";
}

interface AotdCardsPanelProps {
  aotdId: string | null;
  isAuthenticated: boolean;
  /**
   * `banner` is the strip that sits ABOVE the art and only renders while card
   * effects are live on today's AOTD — always expanded, one row per effect.
   * `panel` is the quiet collapsed affordance below the art, which steps aside
   * whenever the banner has something to say. The page mounts both; they share
   * every RTK cache entry, so the pair costs no extra network.
   */
  placement?: "banner" | "panel";
}

/**
 * Cards on the AOTD page, in two placements.
 *
 * `panel`, below the art: the viewer's hand of 4 in the Deck look
 * (`hand/AotdHand.tsx`), with the play window, offers and the keep-4 picker.
 * Signed-out visitors get nothing. Against a backend that does not send the
 * hand yet (`/my-cards` without `hand`), the old collapsed one-liner stays.
 *
 * `banner`, above the art: an always-expanded strip, one row per active effect
 * with the card's art, who cast it, who it landed on where that is public, and
 * how long it has left. Effects aimed at the viewer turn the strip amber and
 * say so in words.
 *
 * The surface feed is the primary list; scoped runtime effects add what the
 * feed cannot express — race-time hiding, comment effects, the viewer's own
 * queued claim, and the named victims of a mute.
 */
function viewerHintEffects(effects: PublicActiveEffect[]): PublicActiveEffect[] {
  return effects.filter(
    (e) =>
      e.effectType === "aotd_underdog_mark" ||
      e.scope === "aotd.race" ||
      e.scope === "aotd.comment" ||
      (e.scope === "aotd.claim" && e.isViewerOwner),
  );
}

/**
 * Some cards say what they are doing better than any phrase we could compose
 * here: the wager board's prediction, a rematch call, a resolved photo finish,
 * a bounty standing on someone. Those stamp a `bannerNote` fragment on their
 * runtime row at play (or settlement) time, and it survives the non-owner
 * redaction, so the strip just reads it out.
 */
export function readBannerNote(
  params: Record<string, unknown> | undefined,
): string | null {
  const note = params?.bannerNote;
  return typeof note === "string" && note.trim().length > 0 ? note.trim() : null;
}

function hintPhrase(effect: PublicActiveEffect): string {
  const authored = readBannerNote(effect.params);
  if (authored) return authored;

  const who = effect.isViewerOwner
    ? "you"
    : effect.ownerUsername
      ? `@${effect.ownerUsername}`
      : "another player";
  // The mute's underdog marks ride `aotd.race` too, so they are matched on the
  // effect type first — "hiding race times" would be flatly wrong copy.
  if (effect.effectType === "aotd_underdog_mark") {
    return "muted for the next unlock — racing anyway pays double";
  }
  switch (effect.scope) {
    case "aotd.race":
      return effect.isViewerOwner ? "hiding race times for you" : `hiding race times · ${who}`;
    case "aotd.comment":
      return effect.isViewerOwner ? "affects your comments" : `affects comments · ${who}`;
    case "aotd.claim":
      return "your claim is queued";
    default:
      return "active";
  }
}

/**
 * The names an effect landed on, as the server was willing to share them.
 * `targetUsername` carries Gridlock/Jinx/underdog-mark's single victim,
 * `mutedUsernames` the mute's whole list; both survive
 * `activeEffectRedaction.sanitizeParams` for non-owners, unlike the raw ids.
 */
export function readTargetUsernames(
  params: Record<string, unknown> | undefined,
): string[] {
  if (!params) return [];
  const out: string[] = [];
  const single = params.targetUsername;
  if (typeof single === "string" && single.length > 0) out.push(single);
  const muted = params.mutedUsernames;
  if (Array.isArray(muted)) {
    for (const name of muted) {
      if (typeof name === "string" && name.length > 0) out.push(name);
    }
  }
  return [...new Set(out)];
}

/** One line of the banner: a card, who played it, who wears it, how long left. */
export interface BannerRow {
  key: string;
  card: CardDefinition | null;
  emoji: string | null;
  casterLabel: string;
  /** Null when the card hides its card face, or when nobody was named. */
  targetLabel: string | null;
  aimedAtViewer: boolean;
  expiresAt: string | null;
  note: string | null;
}

/**
 * Folds the public surface feed and the live scoped effects into one row per
 * effect. Both lists key off the same ActiveEffect `_id`, so an effect that
 * appears in both contributes its target from the effects read and its art from
 * the feed, and is never listed twice. Effects the feed cannot express get
 * their own row from `viewerHintEffects`.
 *
 * One mute writes a gate plus one underdog mark per victim; the gate already
 * names everybody, so a mark only earns a row when it is the viewer's own.
 */
export function buildBannerRows(params: {
  activeCards: SurfaceCardFeedItem[];
  effects: PublicActiveEffect[];
  viewerUsername: string | null;
}): BannerRow[] {
  const { activeCards, effects, viewerUsername } = params;
  const effectById = new Map(effects.map((e) => [e._id, e]));
  const hints = viewerHintEffects(effects).filter((e) => {
    if (e.effectType !== "aotd_underdog_mark") return true;
    const marked = readTargetUsernames(e.params);
    return Boolean(viewerUsername && marked.includes(viewerUsername));
  });
  const rows: BannerRow[] = [];
  const seen = new Set<string>();

  for (const item of activeCards) {
    seen.add(item.id);
    const effect = effectById.get(item.id);
    // A card the viewer is not allowed to see keeps its back turned, and a
    // turned-back card names nobody.
    const showCard = item.visibility.showCard !== false;
    const targets = showCard ? readTargetUsernames(effect?.params) : [];
    const aimedAtViewer = Boolean(
      viewerUsername && targets.includes(viewerUsername),
    );
    rows.push({
      key: item.id,
      card: item.card,
      emoji: item.card?.emoji ?? null,
      casterLabel: effect?.isViewerOwner ? "you" : item.owner.label,
      targetLabel:
        targets.length > 0 ? targets.map((name) => `@${name}`).join(", ") : null,
      aimedAtViewer,
      expiresAt: item.expiresAt ?? null,
      note: readBannerNote(effect?.params),
    });
  }

  for (const hint of hints) {
    if (seen.has(hint._id)) continue;
    const targets = readTargetUsernames(hint.params);
    rows.push({
      key: hint._id,
      card: null,
      emoji: hint.sourceCardEmoji,
      casterLabel: hint.isViewerOwner
        ? "you"
        : hint.ownerUsername
          ? `@${hint.ownerUsername}`
          : "another player",
      targetLabel:
        targets.length > 0 ? targets.map((name) => `@${name}`).join(", ") : null,
      aimedAtViewer:
        hint.isViewerOwner ||
        Boolean(viewerUsername && targets.includes(viewerUsername)),
      expiresAt: hint.expiresAt,
      note: hintPhrase(hint),
    });
  }

  return rows;
}

export default function AotdCardsPanel(props: AotdCardsPanelProps) {
  if ((props.placement ?? "panel") === "panel") return <AotdCardsHand {...props} />;
  return <AotdCardsPanelLive {...props} />;
}

/** The panel placement: the hand, once /my-cards carries it. */
function AotdCardsHand(props: AotdCardsPanelProps) {
  const { aotdId, isAuthenticated } = props;
  // same cache entry as the page's Play modal provider
  const { data, refetch } = useGetMyCardsQuery(undefined, {
    skip: !isAuthenticated,
  });
  const refresh = React.useCallback(() => {
    void refetch();
  }, [refetch]);
  if (!isAuthenticated || !data) return null;
  if (!data.hand) return <AotdCardsPanelLive {...props} />;
  return <AotdHand data={data} aotdId={aotdId} onRefresh={refresh} />;
}

function AotdCardsPanelLive({
  aotdId,
  isAuthenticated,
  placement = "panel",
}: AotdCardsPanelProps) {
  // keep countdowns / "active" labels honest while the strip is on screen
  useNowTicker(30_000);
  const [open, setOpen] = React.useState(false);
  const { openCardPlay } = useAotdCardPlay();
  const viewerUsername = useAppSelector((state) => state.auth.username);

  const { data: effectsData } = useGetPublicActiveEffectsQuery(
    { scopes: AOTD_SCOPES },
    { refetchOnMountOrArgChange: true },
  );
  const { data: feedData, isLoading } = useGetSurfaceCardFeedQuery({
    surface: "aotd",
    ...(aotdId ? { entityId: aotdId } : {}),
  });
  // Same cache entry as HandStrip's and the play provider's subscription — one
  // network fetch feeds the playable count, the hand row and the modal target.
  const { data: myCardsData } = useGetMyCardsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const activeCards = React.useMemo(
    () => feedData?.activeCards ?? [],
    [feedData?.activeCards],
  );
  const viewerNotices = feedData?.viewerNotices ?? [];
  const recentActivity = feedData?.recentActivity ?? [];
  const effects = React.useMemo(
    () => effectsData?.effects ?? [],
    [effectsData?.effects],
  );

  const bannerRows = React.useMemo(
    () => buildBannerRows({ activeCards, effects, viewerUsername }),
    [activeCards, effects, viewerUsername],
  );

  // The viewer's aotd-scoped hand: drives the "· N playable" collapsed count
  // and gates the HandStrip row (hidden entirely at zero — no layout shift).
  const scopedHandTiles = React.useMemo(
    () =>
      (myCardsData?.tiles ?? []).filter((t) => {
        if (!isInHand(t)) return false;
        const scope = t.definition?.effect?.scope;
        return scope ? AOTD_SCOPE_SET.has(scope) : false;
      }),
    [myCardsData?.tiles],
  );
  const scopedPlayableCount = React.useMemo(
    () => scopedHandTiles.filter(isPlayableNow).length,
    [scopedHandTiles],
  );

  // A tile's Play resolves the server-picked `playableInstanceId` (the dying
  // copy first) and hands it to the page's shared modal with the aotdId context.
  const handlePlay = React.useCallback(
    (tile: OwnedCardTile) => {
      if (!tile.playableInstanceId || !tile.definition) return;
      const instances = myCardsData?.cards ?? [];
      const match = instances.find((c) => c._id === tile.playableInstanceId);
      const target: CardInstance =
        match ?? {
          _id: tile.playableInstanceId,
          userId: "",
          cardId: tile.cardId,
          source: "daily_drop",
          status: "in_hand",
          acquiredAt: tile.acquiredAt,
          playedAt: null,
          effectExpiresAt: null,
          unplayedExpiresAt: tile.soonestUnplayedExpiresAt,
          definition: tile.definition,
        };
      openCardPlay(target, tile.playability ?? null);
    },
    [myCardsData?.cards, openCardPlay],
  );

  const hasContent =
    bannerRows.length > 0 ||
    viewerNotices.length > 0 ||
    scopedHandTiles.length > 0;
  const aimedAtViewer =
    viewerNotices.length > 0 || bannerRows.some((r) => r.aimedAtViewer);

  const handRow =
    scopedHandTiles.length > 0 ? (
      <div className="aotd-cards-panel__hand-row">
        <HandStrip scopeFilter={AOTD_SCOPES} compact onPlay={handlePlay} />
      </div>
    ) : null;

  const noticeRows = viewerNotices.map((n) => (
    <div className="aotd-cards-panel__note" key={n.id}>
      <span className="aotd-cards-panel__note-tag">you</span>
      <span className="aotd-cards-panel__note-msg">{n.message}</span>
      <span className="aotd-cards-panel__time">{formatRelativeTime(n.occurredAt)}</span>
    </div>
  ));

  const recentRows =
    recentActivity.length > 0 ? (
      <div className="aotd-cards-panel__recent">
        {recentActivity.slice(0, 4).map((item) => (
          <div className="aotd-cards-panel__recent-item" key={item.id}>
            <span className="aotd-cards-panel__recent-msg">{item.message}</span>
            <span className="aotd-cards-panel__time">
              {formatRelativeTime(item.occurredAt)}
            </span>
          </div>
        ))}
      </div>
    ) : null;

  // ── the banner: above the art, only while something is live ──────────────
  if (placement === "banner") {
    if (bannerRows.length === 0) return null;
    return (
      <section
        className={`aotd-cards-banner${aimedAtViewer ? " is-aimed" : ""}`}
        aria-label="Cards live on this AOTD"
      >
        <div className="aotd-cards-banner__head">
          <span className="aotd-cards-banner__title">
            <span aria-hidden="true">🃏</span>{" "}
            {bannerRows.length === 1
              ? "A card is live on today's art"
              : `${bannerRows.length} cards are live on today's art`}
          </span>
          {aimedAtViewer ? (
            <span className="aotd-cards-banner__aimed">Aimed at you</span>
          ) : null}
        </div>

        <div className="aotd-cards-banner__rows">
          {bannerRows.map((row) => {
            const left = formatEffectRemaining(row.expiresAt) || humanizeUntil(row.expiresAt);
            return (
              <div
                className={`aotd-cards-banner__row${row.aimedAtViewer ? " is-aimed" : ""}`}
                key={row.key}
              >
                <div className="aotd-cards-banner__art">
                  {row.card ? (
                    <RespectCard card={row.card} size="xs" />
                  ) : (
                    // Styled card back — the U+1F0A0 playing-card glyph renders
                    // as tofu on Android, so the back is drawn in CSS instead.
                    <div
                      className="aotd-cards-panel__hidden"
                      role="img"
                      aria-label="hidden card"
                    />
                  )}
                </div>
                <div className="aotd-cards-banner__body">
                  <span className="aotd-cards-banner__caster">
                    {row.emoji ? (
                      <span aria-hidden="true">{row.emoji} </span>
                    ) : null}
                    {row.card?.name ?? "A card"} · {row.casterLabel}
                  </span>
                  {row.targetLabel ? (
                    <span className="aotd-cards-banner__target">
                      on {row.targetLabel}
                    </span>
                  ) : null}
                  {row.note ? (
                    <span className="aotd-cards-banner__note">{row.note}</span>
                  ) : null}
                  {row.aimedAtViewer ? (
                    <span className="aotd-cards-banner__flag">
                      This one is aimed at you
                    </span>
                  ) : null}
                </div>
                <span className="aotd-cards-panel__time">
                  {left && left !== "expired" ? left : "active"}
                </span>
              </div>
            );
          })}
        </div>

        {noticeRows}
        {handRow}
        {recentRows}

        {isAuthenticated ? (
          <div className="aotd-cards-panel__foot">
            <Link to="/cards">Open your hand →</Link>
          </div>
        ) : null}
      </section>
    );
  }

  // ── the quiet panel below the art ────────────────────────────────────────
  // The banner owns the page whenever an effect is live, so the panel stands
  // down rather than listing the same cards twice.
  if (bannerRows.length > 0) {
    return null;
  }

  // Nothing to show and still loading: render nothing (avoids a layout flash).
  if (!hasContent && isLoading) {
    return null;
  }

  // Nothing active: anonymous visitors get nothing; signed-in users keep a slim
  // affordance back to their hand.
  if (!hasContent) {
    if (!isAuthenticated) {
      return null;
    }
    return (
      <section className="aotd-cards-panel aotd-cards-panel--empty" aria-label="Cards on this AOTD">
        <span className="aotd-cards-panel__title">
          <span aria-hidden="true">🃏</span> No cards active on this AOTD
        </span>
        <Link className="aotd-cards-panel__hand" to="/cards">
          Open your hand →
        </Link>
      </section>
    );
  }

  return (
    <section className="aotd-cards-panel" aria-label="Cards on this AOTD">
      <button
        type="button"
        className="aotd-cards-panel__head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="aotd-cards-panel__title">
          <span aria-hidden="true">🃏</span> Cards on this AOTD
        </span>
        <span className="aotd-cards-panel__summary">
          {scopedPlayableCount > 0 ? (
            <span className="aotd-cards-panel__playable">
              {scopedPlayableCount} playable
            </span>
          ) : null}
          {aimedAtViewer ? (
            <span className="aotd-cards-panel__aimed">Aimed at you</span>
          ) : null}
          <span className="aotd-cards-panel__chevron" aria-hidden="true">
            {open ? "▾" : "▸"}
          </span>
        </span>
      </button>

      {open ? (
        <div className="aotd-cards-panel__body">
          {handRow}
          {noticeRows}
          {recentRows}

          {isAuthenticated ? (
            <div className="aotd-cards-panel__foot">
              <Link to="/cards">Open your hand →</Link>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
